// ============================================
// 📱 APP: NVA (React App)
// 📄 FILE: src/hooks/useScrollRestoration.tsx
// 🔧 PURPOSE: Tornare indietro e ritrovare la pagina dov'era.
//
//             Nell'app la finestra non scorre mai: scorre un contenitore
//             dentro ogni schermata, e il browser di quella posizione non sa
//             nulla. Qui c'è un guardiano solo, montato nel Layout (che non
//             si rimonta mai): trova il contenitore marcato
//             `data-scroll-root`, ne salva lo scorrimento mentre l'ospite
//             legge e lo rimette quando si torna indietro.
//
//             Le regole, in breve:
//             - avanti (PUSH) si parte dall'alto: è una schermata nuova;
//             - indietro (POP) e sostituzione (REPLACE) si torna dov'era;
//             - chi marca anche `data-scroll-key` ricorda per percorso e non
//               per voce di cronologia: le schede della barra in basso e la
//               coppia Ristoranti/Partner tengono il loro posto anche quando
//               si riaprono dalla barra.
//
//             Il contenuto arriva dopo (rete, foto): per qualche secondo la
//             posizione si rimette a ogni crescita della pagina, e si smette
//             appena l'ospite tocca. Durante quell'attesa il salvataggio è
//             congelato, altrimenti una posizione a metà mangerebbe quella
//             buona — era il difetto di prima.
//
//             Il posto sta in sessionStorage: sopravvive al reload che l'app
//             fa da sola quando il service worker porta una versione nuova.
// ============================================

import React from 'react';
import { useLocation, useNavigationType } from 'react-router-dom';

const PREFIX = 'nva:scroll:';
const SETTLE_MS = 2500;
const RETRY_MS = 150;

// Specchio in memoria: in navigazione privata sessionStorage può negare tutto.
const mirror = new Map<string, number>();

const readPos = (key: string): number => {
  const cached = mirror.get(key);
  if (cached !== undefined) return cached;
  try {
    const raw = window.sessionStorage.getItem(PREFIX + key);
    const value = raw ? parseInt(raw, 10) : 0;
    return Number.isFinite(value) ? value : 0;
  } catch {
    return 0;
  }
};

const writePos = (key: string, value: number): void => {
  mirror.set(key, value);
  try {
    window.sessionStorage.setItem(PREFIX + key, String(value));
  } catch {
    // Spazio finito o modalità privata: resta lo specchio in memoria.
  }
};

/**
 * Da chiamare prima di cambiare l'indirizzo restando sulla stessa schermata
 * (la scheda di una spiaggia, un filtro): il punto buono è già scritto anche
 * se la schermata è arrivata dopo il guardiano (pagina caricata a parte) e il
 * suo scorrimento non è mai stato salvato.
 */
export function rememberScroll(key: string, value: number): void {
  writePos(key, Math.max(0, Math.round(value)));
}

/** Da chiamare quando si vuole ripartire dall'alto di proposito (es. dopo un acquisto). */
export function forgetScroll(key: string): void {
  mirror.delete(key);
  try {
    window.sessionStorage.removeItem(PREFIX + key);
  } catch {
    // Niente da fare: lo specchio è già pulito.
  }
}

/**
 * Il contenitore che scorre adesso. Le schermate a più stati ne marcano più
 * d'uno, ma ne vive uno per volta: si prende l'ultimo davvero visibile, così
 * quando uno sta dentro l'altro vince quello interno.
 */
function findScrollRoot(): HTMLElement | null {
  const nodes = document.querySelectorAll<HTMLElement>('[data-scroll-root]');
  for (let i = nodes.length - 1; i >= 0; i -= 1) {
    const el = nodes[i];
    if (el.isConnected && el.clientHeight > 0) return el;
  }
  return null;
}

export const ScrollRestorer: React.FC = () => {
  const location = useLocation();
  const navigationType = useNavigationType();
  // La voce di cronologia identifica la schermata; alla prima apertura (e
  // dopo un reload) la chiave non c'è ancora e vale l'indirizzo.
  const routeKey = location.key && location.key !== 'default'
    ? location.key
    : location.pathname + location.search;

  React.useLayoutEffect(() => {
    const el = findScrollRoot();
    if (!el) return undefined;

    const pinned = el.dataset.scrollKey;
    const key = pinned || routeKey;
    const wanted = pinned || navigationType !== 'PUSH' ? readPos(key) : 0;

    let frozen = wanted > 0;
    let frame = 0;
    const save = () => {
      if (frozen || !el.isConnected) return;
      window.cancelAnimationFrame(frame);
      frame = window.requestAnimationFrame(() => writePos(key, el.scrollTop));
    };
    el.addEventListener('scroll', save, { passive: true });

    if (!frozen) {
      el.scrollTop = 0;
      writePos(key, 0);
      return () => {
        window.cancelAnimationFrame(frame);
        el.removeEventListener('scroll', save);
      };
    }

    const settle = () => {
      const reachable = Math.max(0, el.scrollHeight - el.clientHeight);
      el.scrollTop = Math.min(wanted, reachable);
    };
    settle();

    // La pagina cresce dopo il primo disegno (dati, immagini): si rimette la
    // posizione a ogni crescita e comunque si riprova per tutta l'attesa.
    const observer = new ResizeObserver(settle);
    observer.observe(el);
    Array.from(el.children).forEach((child) => observer.observe(child));
    const ticker = window.setInterval(settle, RETRY_MS);

    // Appena l'ospite tocca, comanda lui.
    let deadline = 0;
    const release = () => {
      if (!frozen) return;
      frozen = false;
      observer.disconnect();
      window.clearInterval(ticker);
      window.clearTimeout(deadline);
    };
    deadline = window.setTimeout(release, SETTLE_MS);
    el.addEventListener('touchstart', release, { passive: true });
    el.addEventListener('wheel', release, { passive: true });
    el.addEventListener('pointerdown', release, { passive: true });

    return () => {
      const stillRestoring = frozen;
      release();
      window.cancelAnimationFrame(frame);
      // Ripristino non finito: il valore buono è quello già salvato.
      if (!stillRestoring && el.isConnected) writePos(key, el.scrollTop);
      el.removeEventListener('scroll', save);
      el.removeEventListener('touchstart', release);
      el.removeEventListener('wheel', release);
      el.removeEventListener('pointerdown', release);
    };
  }, [routeKey, navigationType]);

  return null;
};

export default ScrollRestorer;
